    import React, { createContext, useContext, useMemo } from 'react';
import { ConfigProvider, theme } from 'antd';
import { Themes } from '@/shared/api/model';
// import { useUserPreferencesStore } from '@/stores/user-preferences-store';

// Stub for missing useUserPreferencesStore
const useUserPreferencesStore: any = () => ({
    dark: false,
    toggleTheme: () => {},
    setThemeFromUserConfig: () => {}
});

// Types
interface ThemeContextType {
    dark: boolean;
    toggleTheme: () => void;
    setThemeFromUserConfig: (userPrefersDark: boolean) => void;
    initializeThemeFromUserProfile: (userProfile: UserProfile) => void;
}

interface UserProfile {
    prefersdark?: boolean;
    // Add other user profile properties as needed
}

// Context creation
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme Provider Component
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { dark, toggleTheme, setThemeFromUserConfig, initializeThemeFromUserProfile } = useThemeHook();

    const { defaultAlgorithm, darkAlgorithm } = theme;

    const themeConfig = useMemo(() => ({
        algorithm: dark ? darkAlgorithm : defaultAlgorithm,
        token: {
            colorPrimary: '#1890ff',
            colorSuccess: '#52c41a',
            colorWarning: '#faad14',
            colorError: '#ff4d4f',
            colorInfo: '#1890ff',
            colorTextBase: dark ? '#ffffff' : '#000000',
            colorBgBase: dark ? 'rgb(17 24 39 / var(--tw-bg-opacity))' : '#ffffff',
        },
        components: {
            Button: {
                colorPrimary: dark ? '#177ddc' : '#1890ff',
                algorithm: true,
            },
            Input: {
                colorBgContainer: dark ? '#1f1f1f' : '#ffffff',
                colorBorder: dark ? '#434343' : '#d9d9d9',
            },
            Select: {
                colorBgContainer: dark ? '#1f1f1f' : '#ffffff',
                colorBorder: dark ? '#434343' : '#d9d9d9',
            },
            Menu: {
                itemBg: dark ? '#141414' : '#ffffff',
                itemColor: dark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.85)',
            },
            Table: {
                colorBgContainer: dark ? '#1f1f1f' : '#ffffff',
                colorText: dark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
            },
            Card: {
                colorBgContainer: dark ? '#1f1f1f' : '#ffffff',
            },
            Modal: {
                colorBgElevated: dark ? '#1f1f1f' : '#ffffff',
            },
            Notification: {
                colorBgElevated: dark ? '#1f1f1f' : '#ffffff',
                colorText: dark ? '#ffffff' : '#000000',
                colorSuccess: dark ? '#49aa19' : '#52c41a',
                colorError: dark ? '#a61d24' : '#ff4d4f',
                colorWarning: dark ? '#d89614' : '#faad14',
                colorInfo: dark ? '#177ddc' : '#1890ff',
            },
        },
    }), [dark]);

    return (
        <ThemeContext.Provider value={{ dark, toggleTheme, setThemeFromUserConfig, initializeThemeFromUserProfile }}>
            <ConfigProvider theme={themeConfig}>
                {children}
            </ConfigProvider>
        </ThemeContext.Provider>
    );
};

// Hook to use the theme
export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Main theme hook
const useThemeHook = (): ThemeContextType => {
    const themePreference = useUserPreferencesStore((state) => state.theme);
    const setThemePreference = useUserPreferencesStore((state) => state.setTheme);
    const dark = themePreference === Themes.DARK;

    const toggleTheme = (): void => {
        setThemePreference(dark ? Themes.LIGHT : Themes.DARK);
    };

    const setThemeFromUserConfig = (userPrefersDark: boolean): void => {
        setThemePreference(userPrefersDark ? Themes.DARK : Themes.LIGHT);
    };

    const initializeThemeFromUserProfile = (userProfile: UserProfile): void => {
        if (userProfile && userProfile.prefersdark !== undefined) {
            setThemeFromUserConfig(userProfile.prefersdark);
        }
    };

    return {
        dark,
        toggleTheme,
        setThemeFromUserConfig,
        initializeThemeFromUserProfile
    };
};

export default useThemeHook;